import React, { useState } from "react";
import { Plus, X, Edit2, Trash2, RotateCcw, Building2, Download, Hash, RefreshCw, Check } from "lucide-react";
import { toast } from "sonner";
import { PageHead } from "../components/PageHead";
import { DataTable } from "../components/DataTable";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";

// Inline editable Vendor-ID chip. Auto-assigned on create; managers/admins can
// override (same scheme & UX as the Customer ID chip).
function VendorNumberCell({ value, vendorId, vendorName, canEdit, onChanged }) {
  const [editing, setEditing] = React.useState(false);
  const [val, setVal] = React.useState(value || "");
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { setVal(value || ""); }, [value]);

  if (!editing) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span className="mono t-strong" data-testid={`vendor-num-${vendorId}`}
          style={{ background: "var(--card-2)", padding: "2px 8px", borderRadius: 999, fontSize: 12, letterSpacing: ".04em", color: "#6b521a" }}>
          {value || "—"}
        </span>
        {canEdit && (
          <button className="icon-btn" onClick={(e) => { e.stopPropagation(); setEditing(true); }}
            title="Override vendor ID" data-testid={`vendor-num-edit-${vendorId}`}
            style={{ padding: 2, color: "var(--text-3)" }}>
            <Edit2 size={11} />
          </button>
        )}
      </span>
    );
  }

  const save = async () => {
    const v = val.trim().toUpperCase();
    if (!/^[A-Z]{3}-\d{3,6}$/.test(v)) {
      toast.error("Format must be XXX-NNN (e.g. MID-001)");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/subcontractors/${vendorId}/number`, { vendor_number: v });
      toast.success(`${vendorName || "Vendor"} renumbered to ${v}`);
      setEditing(false);
      onChanged && onChanged();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }} onClick={(e) => e.stopPropagation()}>
      <input autoFocus value={val}
        onChange={(e) => setVal(e.target.value.toUpperCase())}
        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
        data-testid={`vendor-num-input-${vendorId}`}
        style={{ width: 84, fontFamily: "var(--mono)", fontSize: 12, padding: "3px 6px", border: "1px solid var(--gold-champ)", borderRadius: 6 }} />
      <button className="icon-btn" onClick={save} disabled={busy} title="Save" style={{ color: "#3a7a3a", padding: 2 }} data-testid={`vendor-num-save-${vendorId}`}><Check size={13} /></button>
      <button className="icon-btn" onClick={() => { setVal(value || ""); setEditing(false); }} title="Cancel" style={{ color: "var(--text-3)", padding: 2 }}><X size={13} /></button>
    </span>
  );
}

const FIELDS = [
  { key: "name", label: "Name", required: true, placeholder: "e.g. Midlands Metrology" },
  { key: "contact_person", label: "Contact person" },
  { key: "email", label: "Email", type: "email" },
  { key: "contact_number", label: "Contact number" },
  { key: "alt_number", label: "Alt. number" },
  { key: "address", label: "Address" },
  { key: "city", label: "City" },
  { key: "province", label: "Province" },
  { key: "country", label: "Country", placeholder: "South Africa" },
  { key: "notes", label: "Notes", textarea: true },
];

const EMPTY = { name: "", contact_person: "", email: "", contact_number: "", alt_number: "",
                address: "", city: "", province: "", country: "South Africa", notes: "", active: true };

function SubconModal({ open, initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [busy, setBusy] = useState(false);
  React.useEffect(() => { if (open) setForm(initial || EMPTY); }, [open, initial]);
  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    if (!form.name?.trim()) return toast.error("Name is required.");
    setBusy(true);
    try {
      if (initial?.id) {
        await api.patch(`/subcontractors/${initial.id}`, form);
        toast.success(`Updated "${form.name}"`);
      } else {
        await api.post("/subcontractors", form);
        toast.success(`Added "${form.name}"`);
      }
      onSaved();
      onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div onClick={onClose}
         style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 200,
                  display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div onClick={(e) => e.stopPropagation()}
           className="card pad"
           style={{ width: "min(720px, 100%)", maxHeight: "88vh", overflowY: "auto", background: "var(--card)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
            <Building2 size={18} color="var(--gold-deep)" />
            {initial?.id ? "Edit subcontractor" : "Add subcontractor"}
          </h3>
          <button className="btn ghost sm" onClick={onClose}><X size={14} /></button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {FIELDS.map((f) => (
            <div key={f.key} className="fld" style={{ gridColumn: f.textarea ? "1 / -1" : undefined }}>
              <label>{f.label}{f.required && <span style={{ color: "var(--red)" }}>*</span>}</label>
              {f.textarea
                ? <textarea className="input" rows={3} value={form[f.key] || ""} onChange={set(f.key)}
                              data-testid={`subcon-field-${f.key}`} />
                : <input className="input" type={f.type || "text"} value={form[f.key] || ""}
                          onChange={set(f.key)} placeholder={f.placeholder}
                          data-testid={`subcon-field-${f.key}`} />}
            </div>
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn gold" onClick={save} disabled={busy} data-testid="subcon-save">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SubcontractorRegister() {
  const { can, user } = useAuth();
  const canEdit = can("subcontractor", "edit");
  const canRenumber = ["manager", "admin"].includes(user?.role);
  const [refresh, setRefresh] = useState(0);
  const [modal, setModal] = useState(null); // { initial }
  const [showInactive, setShowInactive] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/subcontractors/export${showInactive ? "" : "?active_only=true"}`, { responseType: "blob", timeout: 600000 });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url; a.download = `vendors-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Export ready — check your downloads");
    } catch (e) { toast.error(apiErr(e)); } finally { setExporting(false); }
  };

  const backfillIds = async () => {
    if (!window.confirm(
      "Assign Vendor IDs to all vendors that don't have one?\n\n" +
      "Format: XXX-NNN (e.g. MID-001) using the first 3 letters of the vendor name. " +
      "Vendors that already have an ID are not touched.\n\nSafe to run repeatedly."
    )) return;
    setBackfilling(true);
    try {
      const r = await api.post("/subcontractors/backfill-numbers");
      if (r.data.assigned === 0) toast.success("All vendors already have IDs — nothing to assign.");
      else toast.success(`Assigned ${r.data.assigned} new Vendor ID(s).`);
      setRefresh((x) => x + 1);
    } catch (e) { toast.error(apiErr(e)); } finally { setBackfilling(false); }
  };

  const onDelete = async (row) => {
    if (!window.confirm(
      `Soft-delete subcontractor "${row.name}"?\n\n` +
      `It will be hidden from active lists but historical job-item links are preserved. ` +
      `You can restore it from the 'Show inactive' view.`
    )) return;
    try {
      const r = await api.delete(`/subcontractors/${row.id}`);
      const links = r.data.linked_equipment + r.data.linked_job_items;
      toast.success(`Soft-deleted "${row.name}"${links ? ` · ${links} historical link(s) preserved` : ""}`);
      setRefresh((x) => x + 1);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const onRestore = async (row) => {
    try {
      await api.post(`/subcontractors/${row.id}/restore`);
      toast.success(`Restored "${row.name}"`);
      setRefresh((x) => x + 1);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const bulkDelete = async (ids, clear) => {
    if (!window.confirm(
      `Soft-delete ${ids.length} subcontractor${ids.length === 1 ? "" : "s"}?\n\n` +
      `They will be hidden from active lists but historical job-item links are ` +
      `preserved. You can restore them from the 'Show inactive' view. Continue?`
    )) return;
    try {
      const r = await api.post("/subcontractors/bulk-delete", { ids });
      const links = (r.data.results || []).reduce((s, x) => s + (x.linked_equipment || 0) + (x.linked_job_items || 0), 0);
      toast.success(`Soft-deleted ${r.data.deleted} subcontractor(s)${links ? ` · ${links} historical link(s) preserved` : ""}`);
      clear && clear();
      setRefresh((x) => x + 1);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const columns = [
    { key: "vendor_number", label: "Vendor ID", sortable: true, colSearch: true,
      render: (v, r) => (
        <VendorNumberCell value={v} vendorId={r.id} vendorName={r.name} canEdit={canRenumber}
          onChanged={() => setRefresh((x) => x + 1)} />
      ) },
    { key: "name", label: "Name", sortable: true, colSearch: true,
      render: (v, r) => (
        <span className="cell-2" style={{ display: "inline-flex", flexDirection: "column" }}>
          <span className="t-strong">{v || "—"}</span>
          {r.active === false && <span className="badge b-red" style={{ fontSize: 10 }}><span className="bd" />Inactive</span>}
        </span>
      ) },
    { key: "contact_person", label: "Contact person", sortable: true, colSearch: true,
      render: (v) => v || <span className="t-mut">—</span> },
    { key: "email", label: "Email", colSearch: true,
      render: (v) => v ? <a href={`mailto:${v}`} className="mono" style={{ fontSize: 11.5 }}>{v}</a> : <span className="t-mut">—</span> },
    { key: "contact_number", label: "Phone", colSearch: true,
      render: (v) => v ? <span className="mono" style={{ fontSize: 11.5 }}>{v}</span> : <span className="t-mut">—</span> },
    { key: "city", label: "City", sortable: true, colSearch: true,
      render: (v) => v || <span className="t-mut">—</span> },
    { key: "province", label: "Province", colSearch: true,
      render: (v) => v || <span className="t-mut">—</span> },
    { key: "country", label: "Country", colSearch: true,
      render: (v) => v || <span className="t-mut">—</span> },
    canEdit && {
      key: "_actions", label: "", sortable: false,
      render: (_, r) => (
        <span style={{ display: "inline-flex", gap: 4 }}>
          <button className="btn ghost sm" onClick={(e) => { e.stopPropagation(); setModal({ initial: r }); }}
                  data-testid={`subcon-edit-${r.id}`} title="Edit">
            <Edit2 size={12} />
          </button>
          {r.active === false ? (
            <button className="btn ghost sm" onClick={(e) => { e.stopPropagation(); onRestore(r); }}
                    data-testid={`subcon-restore-${r.id}`} title="Restore">
              <RotateCcw size={12} />
            </button>
          ) : (
            <button className="btn ghost sm" onClick={(e) => { e.stopPropagation(); onDelete(r); }}
                    data-testid={`subcon-del-${r.id}`} title="Soft-delete">
              <Trash2 size={12} color="var(--red)" />
            </button>
          )}
        </span>
      ),
    },
  ].filter(Boolean);

  const params = showInactive ? {} : { active_only: true };

  return (
    <>
      <PageHead crumb="Records" title="Subcontractor Register"
        sub="Master list of subcontractors used for outsourced calibration / repair. Imported from Caspio's Sub_Con_List on cutover. Edits flow into the Job Flow Board, Receiving, and the Subcontractor workflow page." />
      <DataTable endpoint="/subcontractors" params={params} columns={columns}
        rowKey="id" testid="subcon-register"
        defaultSort={{ by: "name", dir: "asc" }} initialLimit={50}
        searchPlaceholder="Search name, contact, email, city…"
        refreshSignal={refresh}
        selectable={canEdit}
        bulkToolbar={canEdit ? (ids, clear) => (
          <button className="btn danger sm" onClick={() => bulkDelete(ids, clear)} data-testid="subcon-bulk-delete">
            <Trash2 size={14} /> Soft-delete selected ({ids.length})
          </button>
        ) : undefined}
        toolbar={
          <>
            <button className={`ftab ${!showInactive ? "active" : ""}`} onClick={() => setShowInactive(false)}
                    data-testid="subcon-show-active">Active only</button>
            <button className={`ftab ${showInactive ? "active" : ""}`} onClick={() => setShowInactive(true)}
                    data-testid="subcon-show-all">Show inactive</button>
          </>
        }
        rightToolbar={
          <>
            <button className="btn ghost" onClick={exportCsv} disabled={exporting} data-testid="vendor-export-btn">
              <Download size={13} /> {exporting ? "Exporting…" : "Export CSV"}
            </button>
            {canRenumber && (
              <button className="btn ghost" onClick={backfillIds} disabled={backfilling} data-testid="vendor-backfill-ids-btn">
                {backfilling ? <><RefreshCw size={13} className="spin" /> Assigning…</> : <><Hash size={13} /> Backdate Vendor IDs</>}
              </button>
            )}
            {canEdit && (
              <button className="btn gold" onClick={() => setModal({ initial: null })}
                      data-testid="subcon-add">
                <Plus size={13} /> Add subcontractor
              </button>
            )}
          </>
        }
      />
      <SubconModal
        open={!!modal}
        initial={modal?.initial}
        onClose={() => setModal(null)}
        onSaved={() => setRefresh((x) => x + 1)}
      />
    </>
  );
}
